import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './Signup.css';

const Signup = ({ onClose }) => {
  const { loginWithGoogle } = useAuth();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    subscriptionType: '',
    openaiApiKey: '',
    finnhubApiKey: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    if (!formData.subscriptionType) {
      setErrors({ subscriptionType: 'Please select a subscription plan' });
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (formData.subscriptionType === 'EARLY_BIRD') {
      const newErrors = {};
      
      if (!formData.openaiApiKey.trim()) {
        newErrors.openaiApiKey = 'OpenAI API key is required for Early Bird plan';
      }
      
      if (!formData.finnhubApiKey.trim()) {
        newErrors.finnhubApiKey = 'FinHub API key is required for Early Bird plan';
      }
      
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }
    return true;
  };

  const handleGoogleSignup = async () => {
    try {
      setLoading(true);
      const googleUser = await loginWithGoogle();
      
      if (googleUser.isNewUser) {
        // New user - populate form data and go to subscription selection
        setFormData(prev => ({
          ...prev,
          fullName: googleUser.fullName,
          email: googleUser.email,
          phone: '', // Still need to collect phone
          password: '', // No password needed for Google users
          authProvider: 'google',
          googleUid: googleUser.googleUid,
          profilePicture: googleUser.profilePicture
        }));
        setStep(2);
      } else {
        // Existing user - close modal (they're already logged in)
        onClose();
      }
    } catch (error) {
      console.error('Google signup error:', error);
      setErrors({ general: error.message || 'Google signup failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleStep1Next = () => {
    if (validateStep1()) {
      setStep(2);
    }
  };

  const handleStep2Next = () => {
    if (validateStep2()) {
      // If Google user and no phone number, collect phone first
      if (formData.authProvider === 'google' && !formData.phone) {
        setStep(2.5); // Go to phone collection step
      } else if (formData.subscriptionType === 'EARLY_BIRD') {
        setStep(3); // Go to API key setup
      } else {
        setStep(4); // Go to Stripe checkout
      }
    }
  };

  const handleStep3Next = () => {
    if (validateStep3()) {
      setStep(4); // Go to Stripe checkout
    }
  };

  const handleStripeCheckout = async () => {
    try {
      setCheckoutLoading(true);
      setCheckoutError(null);
      
      // Store checkout data for after payment completion
      const checkoutData = {
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        subscriptionType: formData.subscriptionType,
        role: 'USER',
        openaiApiKey: formData.subscriptionType === 'EARLY_BIRD' ? formData.openaiApiKey : '',
        finnhubApiKey: formData.subscriptionType === 'EARLY_BIRD' ? formData.finnhubApiKey : '',
        authProvider: formData.authProvider || 'email',
        googleUid: formData.googleUid || null,
        profilePicture: formData.profilePicture || '',
        timestamp: Date.now() // Add timestamp for debugging
      };
      
      console.log('Storing checkout data:', checkoutData);
      localStorage.setItem('bullbearly-checkout-data', JSON.stringify(checkoutData));
      
      // Also store in sessionStorage as backup
      sessionStorage.setItem('bullbearly-checkout-data', JSON.stringify(checkoutData));
      
      // Import stripeService
      const stripeService = (await import('../services/stripeService')).default;
      
      // Create embedded checkout session with Stripe
      const session = await stripeService.createCheckoutSession(
        formData.email, // Use email as user ID for now
        formData.subscriptionType,
        formData.email,
        formData.fullName
      );
      
      console.log('Checkout session created successfully');
      
      if (!session || !session.clientSecret) {
        throw new Error('No client secret received from server');
      }
      
      // Initialize embedded checkout
      console.log('About to initialize embedded checkout with clientSecret:', session.clientSecret);
      
      try {
        const checkout = await stripeService.initEmbeddedCheckout(session.clientSecret, 'stripe-checkout-container');
        console.log('Embedded checkout initialized:', checkout);
        
        // Hide the payment button since embedded checkout is loaded
        setCheckoutLoading(false);
        
        // Set a timeout to check if the checkout actually loaded
        setTimeout(() => {
          const container = document.getElementById('stripe-checkout-container');
          if (container && container.innerHTML.trim() === '') {
            console.error('Embedded checkout did not load properly');
            setCheckoutError('Failed to load payment form. Please try again.');
          }
        }, 3000);
        
      } catch (initError) {
        console.error('Failed to initialize embedded checkout:', initError);
        setCheckoutError('Failed to initialize payment form. Please try again.');
        setCheckoutLoading(false);
      }
      
    } catch (error) {
      console.error('Stripe embedded checkout error:', error);
      setCheckoutError(error.message || 'Failed to initialize payment form. Please try again.');
      setCheckoutLoading(false);
    }
  };

  const handleRedirectCheckout = async () => {
    try {
      setCheckoutLoading(true);
      setCheckoutError(null);
      
      // Store checkout data for after payment completion
      const checkoutData = {
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        subscriptionType: formData.subscriptionType,
        role: 'USER',
        openaiApiKey: formData.subscriptionType === 'EARLY_BIRD' ? formData.openaiApiKey : '',
        finnhubApiKey: formData.subscriptionType === 'EARLY_BIRD' ? formData.finnhubApiKey : '',
        authProvider: formData.authProvider || 'email',
        googleUid: formData.googleUid || null,
        profilePicture: formData.profilePicture || '',
        timestamp: Date.now()
      };
      
      localStorage.setItem('bullbearly-checkout-data', JSON.stringify(checkoutData));
      sessionStorage.setItem('bullbearly-checkout-data', JSON.stringify(checkoutData));
      
      // Import stripeService
      const stripeService = (await import('../services/stripeService')).default;
      
      // Create redirect checkout session
      const session = await stripeService.createCheckoutSession(
        formData.email,
        formData.subscriptionType,
        formData.email,
        formData.fullName
      );
      
      console.log('Redirecting to Stripe checkout:', session.sessionId);
      
      // Redirect to Stripe Checkout
      await stripeService.redirectToCheckout(session.sessionId);
      
    } catch (error) {
      console.error('Redirect checkout error:', error);
      setCheckoutError(error.message || 'Failed to redirect to payment. Please try again.');
      setCheckoutLoading(false);
    }
  };


  const renderStep1 = () => (
    <div className="signup-step">
      <h2>Create Your Account</h2>
      <p className="step-description">
        Join BullBearly and start analyzing the markets with AI-powered insights.
      </p>
      
      <div className="google-signup-section">
        <button 
          className="google-signup-button"
          onClick={handleGoogleSignup}
          disabled={loading}
        >
          <svg className="google-icon" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>
        
        <div className="divider">
          <span>or</span>
        </div>
      </div>
      
      <form className="signup-form">
        <div className="form-group">
          <label htmlFor="fullName">Full Name</label>
          <input
            type="text"
            id="fullName"
            name="fullName"
            value={formData.fullName}
            onChange={handleInputChange}
            className={errors.fullName ? 'error' : ''}
            placeholder="Enter your full name"
          />
          {errors.fullName && <span className="error-message">{errors.fullName}</span>}
        </div>
        
        <div className="form-group">
          <label htmlFor="email">Email Address</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className={errors.email ? 'error' : ''}
            placeholder="Enter your email"
          />
          {errors.email && <span className="error-message">{errors.email}</span>}
        </div>
        
        <div className="form-group">
          <label htmlFor="phone">Phone Number</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            className={errors.phone ? 'error' : ''}
            placeholder="Enter your phone number"
          />
          {errors.phone && <span className="error-message">{errors.phone}</span>}
        </div>
        
        <div className="form-group">
          <label htmlFor="password">Password</label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            className={errors.password ? 'error' : ''}
            placeholder="Create a password"
          />
          {errors.password && <span className="error-message">{errors.password}</span>}
        </div>
        
        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm Password</label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            className={errors.confirmPassword ? 'error' : ''}
            placeholder="Confirm your password"
          />
          {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
        </div>
        
        {errors.general && <div className="error-message general">{errors.general}</div>}
        
        <button 
          type="button"
          className="next-button"
          onClick={handleStep1Next}
          disabled={loading}
        >
          Continue to Subscription
        </button>
      </form>
    </div>
  );

  const renderStep2_5 = () => (
    <div className="signup-step">
      <h2>Complete Your Profile</h2>
      <p className="step-description">
        Please provide your phone number to complete your account setup.
      </p>
      
      <form className="signup-form">
        <div className="form-group">
          <label htmlFor="phone">Phone Number</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            className={errors.phone ? 'error' : ''}
            placeholder="Enter your phone number"
          />
          {errors.phone && <span className="error-message">{errors.phone}</span>}
        </div>
        
        {errors.general && <div className="error-message general">{errors.general}</div>}
        
        <div className="step-buttons">
          <button 
            className="back-button"
            onClick={() => setStep(2)}
          >
            Back
          </button>
          <button 
            type="button"
            className="next-button"
            onClick={() => {
              if (!formData.phone.trim()) {
                setErrors({ phone: 'Phone number is required' });
                return;
              }
              if (formData.subscriptionType === 'EARLY_BIRD') {
                setStep(3); // Go to API key setup
              } else {
                setStep(4); // Go to Stripe checkout
              }
            }}
          >
            Continue
          </button>
        </div>
      </form>
    </div>
  );

  const renderStep2 = () => (
    <div className="signup-step">
      <h2>Choose Your Plan</h2>
      <p className="step-description">
        Select the subscription plan that best fits your needs.
      </p>
      
      <div className="subscription-options">
        <div 
          className={`subscription-card ${formData.subscriptionType === 'EARLY_BIRD' ? 'selected' : ''}`}
          onClick={() => setFormData(prev => ({ ...prev, subscriptionType: 'EARLY_BIRD' }))}
        >
          <div className="subscription-header">
            <h3>Early Bird - BYOK</h3>
            <div className="price">
              <span className="currency">$</span>
              <span className="amount">10</span>
              <span className="period">/month</span>
            </div>
          </div>
          
          <div className="subscription-features">
            <ul>
              <li>✓ Bring Your Own Keys (BYOK)</li>
              <li>✓ Full access to all features</li>
              <li>✓ Custom AI prompts</li>
              <li>✓ RSS feed management</li>
              <li>✓ Real-time market data</li>
              <li>✓ AI-powered analysis</li>
            </ul>
          </div>
          
          <div className="subscription-requirements">
            <h4>You'll need to provide:</h4>
            <ul>
              <li>• OpenAI API key (your own)</li>
              <li>• FinHub API key (your own)</li>
            </ul>
            <p className="note">
              <strong>Note:</strong> You'll be responsible for API usage costs and rate limits.
            </p>
          </div>
        </div>
        
        <div 
          className={`subscription-card ${formData.subscriptionType === 'MANAGED' ? 'selected' : ''}`}
          onClick={() => setFormData(prev => ({ ...prev, subscriptionType: 'MANAGED' }))}
        >
          <div className="subscription-header">
            <h3>Early Bird - Managed</h3>
            <div className="price">
              <span className="currency">$</span>
              <span className="amount">19</span>
              <span className="period">/month</span>
            </div>
          </div>
          
          <div className="subscription-features">
            <ul>
              <li>✓ All BYOK features included</li>
              <li>✓ No API keys required</li>
              <li>✓ Managed infrastructure</li>
              <li>✓ Priority support</li>
              <li>✓ Higher rate limits</li>
              <li>✓ Advanced analytics</li>
            </ul>
          </div>
          
          <div className="subscription-benefits">
            <h4>We handle everything:</h4>
            <ul>
              <li>• OpenAI API costs included</li>
              <li>• FinHub API costs included</li>
              <li>• Automatic scaling</li>
              <li>• 24/7 monitoring</li>
            </ul>
            <p className="note">
              <strong>Best Value:</strong> No setup required, just start using!
            </p>
          </div>
        </div>
      </div>
      
      {errors.subscriptionType && <div className="error-message">{errors.subscriptionType}</div>}
      
      <div className="step-buttons">
        <button 
          className="back-button"
          onClick={() => setStep(1)}
        >
          Back
        </button>
        <button 
          className="next-button"
          onClick={handleStep2Next}
        >
          Continue
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="signup-step">
      <h2>API Key Setup</h2>
      <p className="step-description">
        Since you chose the BYOK plan, you'll need to provide your own API keys.
      </p>
      
      <div className="api-key-section">
        <div className="api-key-card">
          <h3>OpenAI API Key</h3>
          <p className="api-description">
            Required for AI-powered analysis. Get your key from{' '}
            <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">
              OpenAI Platform
            </a>
          </p>
          
          <div className="form-group">
            <label htmlFor="openaiApiKey">OpenAI API Key</label>
            <input
              type="password"
              id="openaiApiKey"
              name="openaiApiKey"
              value={formData.openaiApiKey}
              onChange={handleInputChange}
              className={errors.openaiApiKey ? 'error' : ''}
              placeholder="sk-proj-..."
            />
            {errors.openaiApiKey && <span className="error-message">{errors.openaiApiKey}</span>}
          </div>
          
          <div className="instructions">
            <h4>How to get your OpenAI API key:</h4>
            <ol>
              <li>Visit <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">OpenAI Platform</a></li>
              <li>Sign in or create an account</li>
              <li>Go to "API Keys" in the left sidebar</li>
              <li>Click "Create new secret key"</li>
              <li>Copy the key (starts with "sk-proj-")</li>
            </ol>
          </div>
        </div>
        
        <div className="api-key-card">
          <h3>FinHub API Key</h3>
          <p className="api-description">
            Required for real-time stock data. Get your key from{' '}
            <a href="https://finnhub.io/register" target="_blank" rel="noopener noreferrer">
              FinHub
            </a>
          </p>
          
          <div className="form-group">
            <label htmlFor="finnhubApiKey">FinHub API Key</label>
            <input
              type="password"
              id="finnhubApiKey"
              name="finnhubApiKey"
              value={formData.finnhubApiKey}
              onChange={handleInputChange}
              className={errors.finnhubApiKey ? 'error' : ''}
              placeholder="Your FinHub API key"
            />
            {errors.finnhubApiKey && <span className="error-message">{errors.finnhubApiKey}</span>}
          </div>
          
          <div className="instructions">
            <h4>How to get your FinHub API key:</h4>
            <ol>
              <li>Visit <a href="https://finnhub.io/register" target="_blank" rel="noopener noreferrer">FinHub Registration</a></li>
              <li>Create a free account</li>
              <li>Go to your dashboard</li>
              <li>Copy your API key from the "API Key" section</li>
            </ol>
          </div>
        </div>
      </div>
      
      <div className="step-buttons">
        <button 
          className="back-button"
          onClick={() => setStep(2)}
        >
          Back
        </button>
        <button 
          className="next-button"
          onClick={handleStep3Next}
        >
          Continue to Payment
        </button>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="signup-step" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ flex: '0 0 auto' }}>
        <h2>Complete Your Subscription</h2>
        <p className="step-description">
          Review your plan and proceed to secure payment.
        </p>
        
        <div className="subscription-summary">
          <h3>Plan Summary</h3>
          <div className="summary-details">
            <div className="summary-row">
              <span>Plan:</span>
              <span>{formData.subscriptionType === 'EARLY_BIRD' ? 'Early Bird - BYOK' : 'Early Bird - Managed'}</span>
            </div>
            <div className="summary-row">
              <span>Price:</span>
              <span>${formData.subscriptionType === 'EARLY_BIRD' ? '10' : '19'}/month</span>
            </div>
            <div className="summary-row total">
              <span>Total:</span>
              <span>${formData.subscriptionType === 'EARLY_BIRD' ? '10' : '19'}/month</span>
            </div>
          </div>
        </div>
        
        <div className="payment-info">
          <h3>Secure Payment</h3>
          <p>
            Your payment will be processed securely through Stripe. 
            You can cancel or change your plan at any time.
          </p>
          
          <div className="payment-methods">
            <div className="payment-method">
              <div className="payment-icon">💳</div>
              <span>Credit/Debit Card</span>
            </div>
            <div className="payment-method">
              <div className="payment-icon">🔒</div>
              <span>Secure & Encrypted</span>
            </div>
          </div>
        </div>
        
        {errors.general && <div className="error-message general">{errors.general}</div>}
        
        <div className="step-buttons">
          <button 
            className="back-button"
            onClick={() => setStep(formData.subscriptionType === 'EARLY_BIRD' ? 3 : 2)}
          >
            Back
          </button>
          <button 
            className="payment-button"
            onClick={handleStripeCheckout}
            disabled={checkoutLoading}
          >
            {checkoutLoading ? 'Loading Payment Form...' : 'Proceed to Secure Payment'}
          </button>
        </div>
      </div>
      
      {/* Embedded Stripe Checkout Container */}
      {checkoutLoading && (
        <div className="checkout-loading">
          <div className="loading-spinner"></div>
          <p>Loading secure payment form...</p>
        </div>
      )}
      
      
      {checkoutError && (
        <div className="checkout-error">
          <p>{checkoutError}</p>
          <div className="error-actions">
            <button 
              className="retry-button"
              onClick={handleStripeCheckout}
            >
              Try Again
            </button>
            <button 
              className="redirect-button"
              onClick={handleRedirectCheckout}
            >
              Use Redirect Checkout
            </button>
          </div>
        </div>
      )}
      
      <div id="stripe-checkout-container" className="stripe-checkout-container"></div>
    </div>
  );

  return (
    <div className="signup-modal-overlay">
      <div className="signup-modal">
        <div className="signup-header">
          <h1>Join BullBearly</h1>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="signup-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${(step / 4) * 100}%` }}
            ></div>
          </div>
          <div className="progress-steps">
            <span className={step >= 1 ? 'active' : ''}>Account</span>
            <span className={step >= 2 ? 'active' : ''}>Plan</span>
            <span className={step >= 3 ? 'active' : ''}>{formData.subscriptionType === 'EARLY_BIRD' ? 'API Keys' : 'Payment'}</span>
            <span className={step >= 4 ? 'active' : ''}>Complete</span>
          </div>
        </div>
        
        <div className="signup-content">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 2.5 && renderStep2_5()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
        </div>
      </div>
    </div>
  );
};

export default Signup;